'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

import { ChevronLeft, ChevronRight, Pause, Play } from 'lucide-react';

import { cn } from '@kit/ui/utils';

import type { HeroBlock, HeroSlide } from '~/(tenant)/_lib/schema/landing-config.schema';
import { HeroSlideView } from './hero-slide';
import styles from './hero-slide.module.css';
import type { LandingRenderContext } from './landing-context';

const ROTATE_MS = 6000;

export function HeroCarousel({
  slides,
  ctx,
  align,
  heightClass,
  options,
}: {
  slides: HeroSlide[];
  ctx: LandingRenderContext;
  align: 'start' | 'center';
  heightClass: string;
  options?: HeroBlock;
}) {
  const [state, setState] = useState({ active: 0, paused: false, hovered: false, reducedMotion: false });
  const pointerFocusTarget = useRef<Node | null>(null);
  const isArabic = ctx.locale.startsWith('ar');
  const paused = state.paused || state.hovered || state.reducedMotion;
  const active = state.active % slides.length;
  const setPlaybackPaused = useCallback((paused: boolean) => setState((current) => ({ ...current, paused })), []);

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setState((current) => ({ ...current, reducedMotion: media.matches }));
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (slides.length < 2 || paused) return;
    const timer = setInterval(() => {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      setState((current) => ({ ...current, active: (current.active + 1) % slides.length }));
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [slides.length, paused]);

  return (
    <div
      role="region"
      aria-roledescription={isArabic ? 'عرض شرائح' : 'carousel'}
      aria-label={isArabic ? 'أبرز المحتوى' : 'Highlights'}
      className={cn('relative grid w-full overflow-hidden', heightClass)}
      onMouseEnter={() => setState((current) => ({ ...current, hovered: true }))}
      onMouseLeave={() => setState((current) => ({ ...current, hovered: false }))}
      onPointerDownCapture={(event) => { pointerFocusTarget.current = event.target as Node; }}
      onPointerUpCapture={() => { pointerFocusTarget.current = null; }}
      onPointerCancelCapture={() => { pointerFocusTarget.current = null; }}
      onFocusCapture={(event) => {
        const pointerFocus = pointerFocusTarget.current !== null && event.target.contains(pointerFocusTarget.current);
        pointerFocusTarget.current = null;
        if (!pointerFocus && !event.currentTarget.contains(event.relatedTarget)) {
          setState((current) => ({ ...current, paused: true }));
        }
      }}
    >
      <div className="grid" aria-live={paused ? 'polite' : 'off'}>
        {slides.map((slide, i) => (
          <div
            key={i}
            role="group"
            aria-roledescription={isArabic ? 'شريحة' : 'slide'}
            aria-label={`${i + 1} / ${slides.length}`}
            aria-hidden={i !== active}
            inert={i !== active}
            className={cn(
              'col-start-1 row-start-1 grid transition-opacity duration-700 motion-reduce:transition-none',
              i === active ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
          >
            <HeroSlideView slide={slide} ctx={ctx} align={align} heightClass={heightClass} options={options} active={i === active} playbackPaused={state.paused} onPlaybackPauseChange={setPlaybackPaused} headingLevel={i === 0 ? 'h1' : 'h2'} />
          </div>
        ))}
      </div>

      {options?.controls === 'arrows' ? (
        <div className={cn('absolute inset-x-0 bottom-6 z-20 flex items-center justify-center gap-3 text-white', options?.contentStyle === 'therapy' && styles.controls)}>
          <button type="button" onClick={() => setState((current) => ({ ...current, active: (current.active - 1 + slides.length) % slides.length, paused: true }))} className={cn('flex h-12 w-12 items-center justify-center rounded-full bg-white text-neutral-900 shadow', options?.contentStyle === 'therapy' && styles.arrow)} aria-label={isArabic ? 'الشريحة السابقة' : 'Previous slide'}>
            <ChevronLeft size={20} className="rtl:-scale-x-100" aria-hidden />
          </button>
          {slides.map((_, i) => (
            <button key={i} type="button" aria-label={`${isArabic ? 'الشريحة' : 'Slide'} ${i + 1}`} aria-current={i === active ? 'true' : undefined} onClick={() => setState((current) => ({ ...current, active: i, paused: true }))} className={cn('flex h-11 w-6 items-center justify-center', options?.contentStyle === 'therapy' && styles.dot)}>
              <span className={cn('h-2.5 w-2.5 rounded-full bg-white', i === active ? 'opacity-100' : 'opacity-50')} />
            </button>
          ))}
          <button type="button" onClick={() => setState((current) => ({ ...current, active: (current.active + 1) % slides.length, paused: true }))} className={cn('flex h-12 w-12 items-center justify-center rounded-full bg-white text-neutral-900 shadow', options?.contentStyle === 'therapy' && styles.arrow)} aria-label={isArabic ? 'الشريحة التالية' : 'Next slide'}>
            <ChevronRight size={20} className="rtl:-scale-x-100" aria-hidden />
          </button>
          {!state.reducedMotion ? (
            <button type="button" onClick={() => setState((current) => ({ ...current, paused: !current.paused }))} className="flex h-11 w-11 items-center justify-center" aria-label={state.paused ? (isArabic ? 'تشغيل عرض الشرائح' : 'Play slideshow') : (isArabic ? 'إيقاف عرض الشرائح' : 'Pause slideshow')}>
              {state.paused ? <Play size={14} aria-hidden /> : <Pause size={14} aria-hidden />}
            </button>
          ) : null}
        </div>
      ) : (
      <div className={cn('absolute bottom-3 z-20 flex items-center text-white', options?.controls === 'minimal' ? 'end-4' : 'start-1/2 -translate-x-1/2 rounded-full bg-black/70 px-2 rtl:translate-x-1/2')}>
        {!state.reducedMotion ? (
          <button
            type="button"
            onClick={() => setState((current) => ({ ...current, paused: !current.paused }))}
            className="min-h-11 px-3 text-xs font-medium"
            aria-label={state.paused ? (isArabic ? 'تشغيل عرض الشرائح' : 'Play slideshow') : (isArabic ? 'إيقاف عرض الشرائح' : 'Pause slideshow')}
          >
            {options?.controls === 'minimal' ? (state.paused ? <Play size={12} aria-hidden /> : <Pause size={12} aria-hidden />) : state.paused ? (isArabic ? 'تشغيل' : 'Play') : (isArabic ? 'إيقاف' : 'Pause')}
          </button>
        ) : null}
        {slides.map((_, i) => (
          <button
            key={i}
            type="button"
            aria-label={`${isArabic ? 'الشريحة' : 'Slide'} ${i + 1}`}
            aria-current={i === active ? 'true' : undefined}
            onClick={() => setState((current) => ({ ...current, active: i, paused: true }))}
            className="flex h-11 w-11 items-center justify-center"
          >
            <span className={cn('h-1.5 rounded-full', i === active ? options?.controls === 'minimal' ? 'w-6 bg-primary' : 'w-6 bg-white' : 'w-1.5 bg-white/50')} />
          </button>
        ))}
      </div>
      )}
    </div>
  );
}
